import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PreviewSection = ({ causes }) => {
  const navigate = useNavigate();

  const formatCurrency = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000)?.toFixed(0)}k`;
    }
    return `$${amount}`;
  };

  const getProgressPercentage = (raised, goal) => {
    return Math.min((raised / goal) * 100, 100);
  };

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-orange-100 text-black px-4 py-2 rounded-full mb-4 text-sm font-medium">
            <Icon name="Compass" size={16} />
            <span>Featured Causes</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Discover Causes Making{' '}
            <span className="bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent">
              Real Impact
            </span>
          </h2>
          <p className="text-xl text-black max-w-3xl mx-auto">
            Browse thousands of verified nonprofits and find causes that resonate with your values
          </p>
        </div>

        {/* Causes Preview Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {causes?.map((cause) => {
            const progress = getProgressPercentage(cause?.raised, cause?.goal);
            
            return (
              <div
                key={cause?.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-orange-100 hover:border-coral-300 transform hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={cause?.image}
                    alt={`${cause?.name} cause showing ${cause?.description}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-black shadow-md">
                      {cause?.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-black mb-2 group-hover:text-coral-600 transition-colors line-clamp-2">
                    {cause?.name}
                  </h3>
                  <p className="text-black mb-4 line-clamp-2">
                    {cause?.description}
                  </p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-black">
                        {formatCurrency(cause?.raised)} raised
                      </span>
                      <span className="text-sm text-black">
                        of {formatCurrency(cause?.goal)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-coral-500 to-orange-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-black mb-4">
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={16} />
                      <span>{cause?.donors} donors</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="TrendingUp" size={16} />
                      <span>{Math.round(progress)}% funded</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    className="w-full bg-gradient-to-r from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 text-white"
                    iconName="Heart"
                    iconPosition="left"
                  >
                    Support This Cause
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Explore All Button */}
        <div className="text-center">
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate('/register')}
            className="border-2 border-coral-300 text-coral-700 hover:bg-coral-50 px-8 py-4"
            iconName="Search"
            iconPosition="left"
          >
            Explore All Causes
          </Button>
          <p className="text-sm text-black mt-4">
            Sign up to browse {causes?.length * 100}+ verified nonprofits and personalized recommendations
          </p>
        </div>

        {/* Platform Features Preview */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left - Image/Mockup */}
          <div className="relative">
            <div className="bg-gradient-to-br from-coral-100 to-orange-100 rounded-3xl p-8 shadow-xl">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Mock Dashboard Preview */}
                <div className="bg-gradient-to-r from-coral-500 to-orange-500 p-6 text-white">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <Icon name="BarChart3" size={24} />
                    </div>
                    <div>
                      <div className="text-sm opacity-90">Your Impact</div>
                      <div className="text-2xl font-bold">$2,450</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-xs opacity-90">Causes</div>
                      <div className="text-xl font-bold">12</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-xs opacity-90">Lives Impacted</div>
                      <div className="text-xl font-bold">240+</div>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-black">Recent Donation</div>
                      <div className="text-xs text-black">Water Initiative • $75</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-black">Challenge Progress</div>
                      <div className="text-xs text-black">Holiday Giving • 75%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border-2 border-coral-200 animate-bounce">
              <div className="flex items-center space-x-2">
                <Icon name="Trophy" size={20} className="text-amber-500" />
                <span className="text-sm font-bold text-black">Gold Status</span>
              </div>
            </div>
          </div>

          {/* Right - Benefits */}
          <div>
            <h3 className="text-3xl font-bold text-black mb-6">
              Track Your Impact in{' '}
              <span className="bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent">
                Real-Time
              </span>
            </h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-coral-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="BarChart3" size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-black mb-2">Personal Dashboard</h4>
                  <p className="text-black">
                    Visualize your giving history, track donation trends, and see the cumulative impact of your contributions.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="Target" size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-black mb-2">Goal Setting</h4>
                  <p className="text-black">
                    Set personal giving goals and track progress with milestone notifications and achievement badges.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="Share2" size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-black mb-2">Share Your Story</h4>
                  <p className="text-black">
                    Inspire others by sharing your giving journey and the causes you care about with your network.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreviewSection;